export interface User {
  id: number
  email_address: string
  consultancy_name: string | null
  primary_colour: string
  secondary_colour: string
  accent_colour: string
  logo_url: string | null
  created_at: string
  needs_onboarding: boolean
}

export interface CurrentMembership {
  role: 'owner' | 'admin' | 'member'
  owner: boolean
  admin: boolean
}

export interface Flash {
  notice?: string
  alert?: string
  // Distinct per rendered flash. Two identical messages in a row are two
  // different flashes and must both be shown; comparing the text cannot tell
  // them apart. Null when there is no flash.
  id?: string | null
}

export interface PlanState {
  plan: string
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'expired'
  currency: 'GBP' | 'USD' | 'EUR'
  trial_ends_at: string | null
  trial_days_remaining: number | null
  comped: boolean
  access_active: boolean
  read_only: boolean
  // Present only on the billing page's plan prop while a canceled subscription
  // is still within its paid-through grace period.
  grace_period_ends_at?: string | null
}

export interface Impersonation {
  active: boolean
  admin_email_address: string | null
}

// One of the current user's accounts, for the account switcher/picker.
export interface AccountSummary {
  slug: string
  name: string
  role: string
  logo_url: string | null
  url?: string
}

export interface SharedProps {
  current_user: User | null
  current_membership: CurrentMembership | null
  plan: PlanState | null
  // Present only on account-scoped pages (shared by AccountController); absent on
  // auth, the account picker, and public pages.
  current_account?: { slug: string; name: string } | null
  accounts?: AccountSummary[]
  impersonation?: Impersonation | null
  flash: Flash
}

// Fleet-canonical pagination meta shared by paginated list pages.
export interface AdminPagination {
  page: number
  per_page: number
  total: number
  total_pages: number
}

export interface PriceTable {
  GBP: { monthly: number; annual: number; symbol: string }
  USD: { monthly: number; annual: number; symbol: string }
  EUR: { monthly: number; annual: number; symbol: string }
}

export interface PricingProps {
  currency: 'GBP' | 'USD' | 'EUR'
  prices: PriceTable
  pro_features: string[]
  enterprise_features: string[]
  stripe_configured: boolean
}

export interface Member {
  id: number
  email_address: string
  roles: Array<'owner' | 'admin' | 'member'>
  is_self: boolean
  last_owner: boolean
  active: boolean
  joined_at: string
}

export interface Invitation {
  id: number
  email_address: string
  roles: Array<'admin' | 'member'>
  invited_by_email_address: string | null
  expires_at: string
  expired: boolean
  created_at: string
}

export type SurveyType = 'programme_evaluation' | 'coaching_session' | 'workshop' | 'custom'

export const SURVEY_TYPE_LABELS: Record<SurveyType, string> = {
  programme_evaluation: 'Programme Evaluation',
  coaching_session: 'Coaching Session',
  workshop: 'Workshop',
  custom: 'Custom',
}

export interface SurveyListItem {
  id: number
  name: string
  programme_name: string
  status: 'draft' | 'active' | 'closed'
  survey_type: SurveyType
  template_name: string
  slug: string
  demo: boolean
  response_count: number
  created_at: string
  updated_at: string
  last_response_at: string | null
}

export interface OnboardingChecklist {
  created: boolean
  branding: boolean
  published: boolean
  first_response: boolean
  dismissed: boolean
}

export interface SurveyTemplateOption {
  id: number
  name: string
  description: string | null
  question_count: number
}

export interface Question {
  id: number
  question_text: string
  question_type: 'scale' | 'open_text' | 'single_choice'
  options: string[] | null
  position: number
  required: boolean
  scale_preset: string | null
  scale_labels: string[] | null
  scale_points: number | null
  resolved_labels: string[] | null
  resolved_points: number
  // Per-question retrospective (post-then-pre) mode. Only ever true on scale
  // questions; a survey is retrospective if any of its questions is.
  retrospective: boolean
}

export interface ScalePreset {
  points: number
  label: string
  labels: (string | null)[]
}

export const SCALE_PRESETS: Record<string, ScalePreset> = {
  agreement_5: { points: 5, label: 'Agreement (1–5)', labels: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'] },
  quality_5: { points: 5, label: 'Quality (1–5)', labels: ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'] },
  effectiveness_5: { points: 5, label: 'Effectiveness (1–5)', labels: ['Not Effective', 'Slightly Effective', 'Moderately Effective', 'Effective', 'Highly Effective'] },
  satisfaction_5: { points: 5, label: 'Satisfaction (1–5)', labels: ['Very Dissatisfied', 'Dissatisfied', 'Neutral', 'Satisfied', 'Very Satisfied'] },
  confidence_5: { points: 5, label: 'Confidence (1–5)', labels: ['Not Confident', 'Slightly Confident', 'Moderately Confident', 'Confident', 'Very Confident'] },
  extent_5: { points: 5, label: 'Extent (1–5)', labels: ['Not at All', 'A Little', 'Somewhat', 'To a Good Extent', 'To a Great Extent'] },
  frequency_5: { points: 5, label: 'Frequency (1–5)', labels: ['Never', 'Rarely', 'Sometimes', 'Often', 'Always'] },
  agreement_7: { points: 7, label: 'Agreement (1–7)', labels: ['Strongly Disagree', 'Disagree', 'Somewhat Disagree', 'Neutral', 'Somewhat Agree', 'Agree', 'Strongly Agree'] },
  likelihood_10: { points: 10, label: 'Likelihood (1–10)', labels: ['Not at all likely', null, null, null, null, null, null, null, null, 'Extremely likely'] },
}

export type Anonymity = 'anonymous' | 'named' | null

export interface SurveyDetail {
  id: number
  name: string
  programme_name: string
  survey_type: SurveyType
  template_name: string
  status: 'draft' | 'active' | 'closed'
  slug: string
  welcome_message: string | null
  closing_message: string | null
  retrospective: boolean
  retrospective_label_before: string | null
  retrospective_label_after: string | null
  anonymity: Anonymity
  webhook_url: string | null
  webhook_signing_secret: string | null
  demo: boolean
  response_count: number
  content_locked: boolean
  created_at: string
  published_at: string | null
  closed_at: string | null
  consultancy_name: string
  primary_colour: string
  secondary_colour: string
  accent_colour: string
  before_tint_colour: string
  logo_url: string | null
  client_logo_url: string | null
  questions: Question[]
  survey_facilitators: SurveyFacilitatorAssignment[]
  available_facilitators: FacilitatorOption[]
  existing_sessions: string[]
  existing_groups: string[]
}

export interface IndividualResponse {
  id: number
  submitted_at: string
  respondent: { first_name: string; last_name: string; email_address: string } | null
  facilitator: { first_name: string; last_name: string; email_address: string } | null
  group_identifier: string | null
  session_identifier: string | null
  answers: {
    question_id: number
    question_text: string
    question_type: 'scale' | 'open_text' | 'single_choice'
    value: string | null
    value_before: string | null
  }[]
}

export interface SurveyFacilitatorAssignment {
  id: number
  facilitator_id: number
  first_name: string
  last_name: string
  name: string  // computed display helper, "first last"
  email_address: string
  token: string
  survey_url: string
}

export interface FacilitatorOption {
  id: number
  first_name: string
  last_name: string
  name: string
  email_address: string
}

export interface FacilitatorFilter {
  id: number
  first_name: string
  last_name: string
  name: string
  response_count: number
}

export interface Facilitator {
  id: number
  first_name: string
  last_name: string
  name: string
  email_address: string
  token: string
  survey_count: number
  created_at: string
}

// An account's saved custom scale (labels + points). Applying one to a
// question copies the labels in — questions never reference presets.
export interface ScalePresetRecord {
  id: number
  name: string
  points: number
  labels: string[]
}

export interface SurveyEdit extends SurveyDetail {
  scale_presets: ScalePresetRecord[]
  invitation_subject: string
  invitation_body: string
  reminder_subject: string
  reminder_body: string
  invitation_subject_custom: boolean
  invitation_body_custom: boolean
  reminder_subject_custom: boolean
  reminder_body_custom: boolean
  consultancy_name_override: string | null
  primary_colour_override: string | null
  secondary_colour_override: string | null
  accent_colour_override: string | null
  has_logo_override: boolean
  has_client_logo: boolean
  account_consultancy_name: string | null
  account_primary_colour: string
  account_secondary_colour: string
  account_accent_colour: string
  account_logo_url: string | null
}

export interface TextAnswer {
  id: number
  value: string
  redacted_at: string | null
  // Present only in the moderator payload. A hidden answer is still SENT to
  // owners/admins so it can be restored; members never receive text_answers.
  hidden_at?: string | null
}

export interface QuestionResult {
  question: Question
  response_count: number
  distribution?: Record<string, number>
  mean?: number | null
  distribution_before?: Record<string, number>
  mean_before?: number | null
  mean_shift?: number | null
  text_responses?: string[]
  // Owner/admin only — presence signals moderation permission.
  text_answers?: TextAnswer[]
}

// A survey's invited respondent (SurveyRespondent joined with Respondent).
export interface RespondentEmailLog {
  id: number
  email_type: 'invitation' | 'reminder'
  subject: string
  sent_at: string
}

export interface RespondentInviteRow {
  id: number
  token: string
  first_name: string
  last_name: string
  email_address: string
  erased: boolean
  invitation_sent_at: string | null
  reminder_sent_at: string | null
  completed_at: string | null
  survey_url: string
  email_history: RespondentEmailLog[]
}

export interface RespondentCounts {
  total: number
  uninvited: number
  awaiting: number
  completed: number
}

export interface ResponseTrend {
  date: string
  count: number
}
